"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_BASE_BINDING_USAGE_EVENT = exports.AppBaseBindingSchema = exports.AppBaseBindingFieldSchema = exports.APP_BASE_BINDING_BUDGETS = exports.APP_BASE_BINDING_TEMPLATE_MAX_BYTES = exports.APP_BASE_BINDING_AUTHORING_CONTRACT = exports.APP_BASE_BINDING_VERSION = void 0;
exports.appBaseBindingInvalidReason = appBaseBindingInvalidReason;
exports.createAppBaseBindingRuntime = createAppBaseBindingRuntime;
const zod_1 = require("zod");
exports.APP_BASE_BINDING_VERSION = 1;
exports.APP_BASE_BINDING_AUTHORING_CONTRACT = Object.freeze({
    version: 1,
    source: 'data-sl-source: literal route-declared source key, inherited by descendants',
    list: 'template[data-sl-list] with one root element; rows keyed by immutable row ID',
    listFilter: 'data-sl-filter-source/field/value joins the row field to one exact selected row of another declared source; @id selects its row ID. data-sl-filter-field/key instead keeps the rows whose field holds the literal key, like a text looked up by its key',
    count: 'data-sl-count: count the authorized source with the existing query aggregate, optionally narrowed by data-sl-filter-field/key; independent of the row window and updated by the binding subscription',
    text: 'data-sl-text: literal exposed column ID or @id; text is escaped. A select or multi-select column shows its option labels in the page language (several joined by ", "); data-sl-attr-* and filters keep the option IDs',
    attribute: 'data-sl-attr-href/src/alt/title/aria-label/datetime; content only on allowed SEO meta',
    attachment: 'data-sl-asset-src on img, video, audio or a source inside video or audio; data-sl-asset-poster on video; data-sl-asset-href on a: literal attachment column, filled with its delivery address. A bound video or audio with autoplay must be muted',
    condition: 'data-sl-if: exposed column truthiness; toggles hidden',
    richText: 'data-sl-rich-text: projected safe HTML or literal Doc snapshot; data-sl-format=markdown uses the shared projector; no formulas, scripts or raw workspace blob URLs',
    structuredData: 'empty template[data-sl-json-ld]: object-valued exposed column; emitted as inert JSON-LD',
    routing: 'dynamicSlugSourceKey + dynamicSlugColumnId select exactly one authorized row for :slug, otherwise 404',
    updates: 'The bridge adopts SSR nodes and patches keyed rows on invalidations. Do not add polling or rebuild bound DOM.',
    reserved: 'data-sl-binding-* belongs to the runtime; never author these attributes',
});
/** Largest HTML template the binding runtime accepts, authoring and render alike. */
exports.APP_BASE_BINDING_TEMPLATE_MAX_BYTES = 256 * 1024;
exports.APP_BASE_BINDING_BUDGETS = Object.freeze({
    /** Elements one page renders from its bindings. */
    nodes: 20000,
    /** HTML one page renders from its bindings. */
    bytes: 2 * 1024 * 1024,
    /** Nesting of bound templates. */
    depth: 64,
    /** Longest wait for the data of the bound elements of a page. */
    readMs: 15_000,
    /** Markdown of one bound cell before it is rendered. */
    markdownBytes: 64 * 1024,
});
exports.AppBaseBindingFieldSchema = zod_1.z
    .string()
    .min(1)
    .max(128)
    .regex(/^(?:@id|[a-zA-Z0-9_$:-]+)$/)
    .refine(value => !['__proto__', 'constructor', 'prototype'].includes(value));
exports.AppBaseBindingSchema = zod_1.z
    .object({
    source: zod_1.z
        .string()
        .min(1)
        .max(128)
        .regex(/^[a-zA-Z0-9_.:-]+$/)
        .refine(value => !['__proto__', 'constructor', 'prototype'].includes(value)),
    kind: zod_1.z.enum([
        'list',
        'count',
        'text',
        'attribute',
        'condition',
        'rich-text',
        'json-ld',
    ]),
    field: exports.AppBaseBindingFieldSchema.optional(),
    attribute: zod_1.z
        .enum([
        'href',
        'src',
        'alt',
        'title',
        'aria-label',
        'datetime',
        'content',
    ])
        .optional(),
})
    .strict()
    .superRefine((binding, ctx) => {
    if (!['list', 'count'].includes(binding.kind) && !binding.field)
        ctx.addIssue({
            code: 'custom',
            path: ['field'],
            message: 'A literal column ID is required',
        });
    if (binding.kind === 'attribute' && !binding.attribute)
        ctx.addIssue({
            code: 'custom',
            path: ['attribute'],
            message: 'An allowed attribute is required',
        });
    if (binding.kind !== 'attribute' && binding.attribute)
        ctx.addIssue({
            code: 'custom',
            path: ['attribute'],
            message: 'Only attribute bindings accept an attribute',
        });
});
/** The event a page in a browser sends after each render of its bindings. */
exports.APP_BASE_BINDING_USAGE_EVENT = 'sl-binding-usage';
/**
 * Reason of a binding contract violation raised by the engine below, or null
 * for any other error. The engine is emitted verbatim to the browser, so it
 * cannot share a constant with this reader; both spell the same prefix.
 */
function appBaseBindingInvalidReason(error) {
    const prefix = 'APP_BASE_BINDING_INVALID: ';
    return error instanceof Error && error.message.startsWith(prefix)
        ? error.message.slice(prefix.length)
        : null;
}
/**
 * One self-contained DOM engine for SSR and browser adoption. The server emits
 * this function verbatim; it never evaluates author JavaScript or expressions.
 */
function createAppBaseBindingRuntime(root, options) {
    // linkedom creates omitted document containers lazily; browsers create them during parsing.
    if (root.tagName === 'HTML') {
        void root.ownerDocument.head;
        void root.ownerDocument.body;
    }
    const prefix = 'data-sl-';
    const reserved = `${prefix}binding-`;
    const navigation = new Map();
    let collectingNavigation = false;
    // Set only while inspectNavigation collects (appBaseLayoutLandmark).
    let landmarkOf;
    const routeUrl = (path) => {
        const mounted = `${(options.routeBasePath ?? '').replace(/\/$/, '')}${path}`;
        if (!options.routeLocale)
            return mounted;
        const url = new URL(mounted, 'https://bindings.invalid');
        url.searchParams.set('lang', options.routeLocale);
        return `${url.pathname}${url.search}${url.hash}`;
    };
    const fields = new Set(['text', 'if', 'rich-text', 'json-ld', 'count']);
    const attributes = new Set([
        'href',
        'src',
        'alt',
        'title',
        'aria-label',
        'datetime',
        'content',
    ]);
    const forbiddenTags = new Set([
        'SCRIPT',
        'STYLE',
        'BASE',
        'LINK',
        'META',
        'IFRAME',
        'OBJECT',
        'EMBED',
        'SVG',
        'MATH',
    ]);
    const mediaTags = new Set(['VIDEO', 'AUDIO']);
    const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
    const fail = (message) => {
        throw new Error(`APP_BASE_BINDING_INVALID: ${message}`);
    };
    // A marker reads the table its element or an element around it names.
    const sourceRequired = (element, marker) => fail(`source required: <${element.tagName.toLowerCase()}> with ${marker} names no table (data-sl-source is missing on it and on every element around it)`);
    const fieldName = (value) => {
        if (!/^(?:@id|[a-zA-Z0-9_$:-]{1,128})$/.test(value) ||
            ['__proto__', 'constructor', 'prototype'].includes(value))
            fail('literal column ID required');
        return value;
    };
    const escape = (value) => value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    const sources = new Set();
    const columns = new Map();
    const column = (source, value) => {
        if (value !== '@id')
            columns.set(JSON.stringify([source, value]), { source, column: value });
    };
    // How each source is read: '*' for its window or single row, otherwise the
    // key of the list filter that every row of it passes through.
    const sourceUses = new Map();
    const listFilters = new Map();
    // Lists of the page by runtime identity, so their rendered rows stay theirs.
    const renderedLists = new Map();
    const use = (source, key) => {
        const uses = sourceUses.get(source) ?? new Set();
        uses.add(key);
        sourceUses.set(source, uses);
    };
    let visited = 0;
    // What the last render took of its budgets, for the StreamKit bar.
    let deepest = 0;
    let renderedRows = 0;
    let renderedBytes = 0;
    // Inspection and rendering count their work (template bytes, rows, nodes,
    // depth, output bytes) and never read a clock: the same template and data
    // give the same verdict on a busy host as on an idle one.
    let listSequence = 0;
    /** Per drawn list: measured line heights, the estimate, rows per line. */
    const rowWindows = new Map();
    // A template as authored counts every element. A page in a browser also
    // holds what the App draws itself; that is not the engine's work, so a
    // render counts only what the engine owns: bound elements, list templates
    // and the rows it drew from them.
    const owned = (element, inRow) => inRow || element.getAttributeNames().some(name => name.startsWith(prefix));
    const holdsRows = (element) => element.tagName === 'TEMPLATE' || element.hasAttribute(`${reserved}owner`);
    const check = (depth) => {
        deepest = Math.max(deepest, depth);
        if (++visited > options.budgets.nodes)
            fail(`render budget exceeded: more than ${options.budgets.nodes} nodes (limit binding.nodes)`);
        if (depth > options.budgets.depth)
            fail(`render budget exceeded: nesting deeper than ${options.budgets.depth} (limit binding.depth)`);
    };
    const children = (element) => element.tagName === 'TEMPLATE'
        ? Array.from(element.content.children)
        : Array.from(element.children);
    const validate = (element, source, depth, authored, 
    /** The list whose row this element shows, like the render walks it. */
    list, 
    /** Inside a list template or a row the engine drew. */
    inRow = false, layout) => {
        if (authored || owned(element, inRow))
            check(depth);
        const owner = element.getAttribute(`${reserved}owner`);
        if (owner !== null)
            list = renderedLists.get(owner);
        source = element.getAttribute(`${prefix}source`) ?? source;
        if (list && list.source !== source)
            list = undefined;
        if (source !== undefined &&
            (!/^[a-zA-Z0-9_.:-]{1,128}$/.test(source) ||
                ['__proto__', 'constructor', 'prototype'].includes(source)))
            fail('invalid source key');
        const route = element.getAttribute('data-sl-route');
        if (route !== null) {
            const dynamic = element.hasAttribute('data-sl-attr-href');
            if (element.tagName !== 'A' ||
                !route.startsWith('/') ||
                options.urlIssue(route) ||
                route.split('/').filter(part => part === ':slug').length !==
                    (dynamic ? 1 : 0))
                fail('invalid route binding');
        }
        layout = element.getAttribute('data-sl-layout') ?? layout;
        if (element.hasAttribute('data-sl-frame-nav') ||
            element.hasAttribute('data-sl-frame-side'))
            layout =
                'frame-nav:' + (element.getAttribute('data-sl-frame-nav') || 'primary');
        const target = route ?? (element.tagName === 'A' ? element.getAttribute('href') : null);
        if (collectingNavigation &&
            target?.startsWith('/') &&
            !target.startsWith('//') &&
            !options.urlIssue(target)) {
            // An App's own page landmarks repeat on every page, like the frame's
            // navigation: their links are shared, not links of one page.
            const shared = layout ?? landmarkOf(element);
            const control = element.getAttribute('data-sl-control');
            const attribute = control
                ? 'data-sl-control'
                : route
                    ? 'data-sl-route'
                    : 'href';
            navigation.set(element, {
                routePath: target.split(/[?#]/)[0],
                ...(source && route ? { sourceKey: source } : {}),
                ...(route && element.hasAttribute('data-sl-attr-href')
                    ? { slugColumnId: element.getAttribute('data-sl-attr-href') }
                    : {}),
                selector: `[${attribute}=${JSON.stringify(control || route || target)}]`,
                label: element.getAttribute('aria-label') ||
                    element.textContent?.trim().slice(0, 160) ||
                    '',
                ...(shared ? { layout: shared } : {}),
                navigation: element.getAttribute('data-sl-navigation') ||
                    (shared ? 'global' : undefined),
            });
        }
        const filterSource = element.getAttribute('data-sl-filter-source');
        const filterKey = element.getAttribute('data-sl-filter-key');
        let listKey = '*';
        if (filterSource !== null && filterKey !== null)
            fail('list filter takes a source or a key, not both');
        if (filterSource !== null) {
            if (!element.hasAttribute('data-sl-list') ||
                !/^[a-zA-Z0-9_.:-]{1,128}$/.test(filterSource) ||
                ['__proto__', 'constructor', 'prototype'].includes(filterSource))
                fail('invalid list filter source');
            if (!source)
                sourceRequired(element, 'data-sl-filter-source');
            const field = fieldName(element.getAttribute('data-sl-filter-field') ?? '');
            const value = fieldName(element.getAttribute('data-sl-filter-value') ?? '');
            column(source, field);
            column(filterSource, value);
            sources.add(filterSource);
            // The filter value is read from the single row of its source.
            use(filterSource, '*');
            listKey = JSON.stringify([filterSource, field, value]);
            listFilters.set(listKey, { field, filterSourceKey: filterSource, value });
        }
        if (filterKey !== null) {
            if ((!element.hasAttribute('data-sl-list') &&
                !element.hasAttribute('data-sl-count')) ||
                filterKey.trim() === '' ||
                filterKey.length > 256)
                fail('invalid list filter key');
            if (!source)
                sourceRequired(element, 'data-sl-filter-key');
            const field = fieldName(element.getAttribute('data-sl-filter-field') ?? '');
            column(source, field);
            // Two entries never meet the three of a source filter.
            listKey = JSON.stringify([field, filterKey]);
            listFilters.set(listKey, { field, key: filterKey });
        }
        if (element.hasAttribute('data-sl-count') &&
            (element.getAttribute('data-sl-filter-field') === null) !==
                (filterKey === null))
            fail('count filter requires field and key together');
        const format = element.getAttribute('data-sl-format');
        if (format &&
            (format !== 'markdown' || !element.hasAttribute('data-sl-rich-text')))
            fail('unsupported rich-text format');
        const attrs = Array.from(element.attributes);
        const bound = attrs.filter(attr => fields.has(attr.name.slice(prefix.length)) &&
            attr.name.startsWith(prefix));
        if (bound.filter(attr => attr.name !== `${prefix}if`).length > 1)
            fail('conflicting content bindings');
        for (const attr of attrs) {
            if (authored && attr.name.startsWith(reserved))
                fail('reserved runtime attribute');
            const kind = attr.name.slice(prefix.length);
            if (attr.name.startsWith(prefix) &&
                (fields.has(kind) ||
                    kind.startsWith('attr-') ||
                    kind.startsWith('asset-') ||
                    kind === 'list')) {
                if (!source)
                    sourceRequired(element, attr.name);
                sources.add(source);
                use(source, kind === 'list'
                    ? listKey
                    : list && list.source === source
                        ? list.key
                        : '*');
                const meta = element.tagName === 'META' &&
                    kind === 'attr-content' &&
                    /^(description|keywords|og:(title|description|image|url)|twitter:(title|description|image))$/.test(element.getAttribute('name') ??
                        element.getAttribute('property') ??
                        '') &&
                    !element.hasAttribute('http-equiv');
                if (kind === 'attr-content' && !meta)
                    fail('unsafe metadata target');
                if (kind === 'json-ld' &&
                    (element.tagName !== 'TEMPLATE' ||
                        element.content.childNodes.length > 0))
                    fail('JSON-LD requires an empty template');
                if ((!meta && forbiddenTags.has(element.tagName.toUpperCase())) ||
                    (element.namespaceURI &&
                        element.namespaceURI !== 'http://www.w3.org/1999/xhtml'))
                    fail('unsafe binding target');
                // A source fills the media element around it.
                const media = element.tagName === 'SOURCE' ? element.parentElement : element;
                const playable = media !== null && mediaTags.has(media.tagName);
                if (kind.startsWith('asset-') &&
                    !((kind === 'asset-src' && (element.tagName === 'IMG' || playable)) ||
                        (kind === 'asset-poster' && element.tagName === 'VIDEO') ||
                        (kind === 'asset-href' && element.tagName === 'A')))
                    fail('unsafe attachment target');
                // Browsers start no sound on their own; such a video would stay still.
                if (kind === 'asset-src' &&
                    playable &&
                    media.hasAttribute('autoplay') &&
                    !media.hasAttribute('muted'))
                    fail('autoplay requires muted');
                if (kind.startsWith('attr-') && !attributes.has(kind.slice(5)))
                    fail('unsafe attribute');
                if (kind === 'count' && attr.value !== '')
                    fail('count requires an empty marker');
                if (kind !== 'list' && kind !== 'count')
                    column(source, fieldName(attr.value));
                if (kind === 'list' &&
                    (element.tagName !== 'TEMPLATE' ||
                        children(element).length !== 1 ||
                        attr.value !== ''))
                    fail('list requires an empty marker on a single-root template');
            }
        }
        let childList = list;
        if (element.hasAttribute(`${prefix}list`) && source) {
            childList = { source, key: listKey };
            const id = element.getAttribute(`${reserved}list`);
            if (id !== null)
                renderedLists.set(id, childList);
        }
        const childInRow = inRow || holdsRows(element);
        for (const child of children(element))
            validate(child, source, depth + 1, authored, childList, childInRow, layout);
    };
    const serialize = (node) => {
        if (node.nodeType === 3)
            return escape(node.nodeValue ?? '');
        if (node.nodeType === 8)
            return `<!--${node.nodeValue ?? ''}-->`;
        if (node.nodeType !== 1)
            return Array.from(node.childNodes).map(serialize).join('');
        const element = node;
        const tag = element.tagName.toLowerCase();
        const attrs = Array.from(element.attributes)
            .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
            .map(attr => ` ${attr.name}="${escape(attr.value)}"`)
            .join('');
        const start = `<${tag}${attrs}>`;
        if ([
            'area',
            'base',
            'br',
            'col',
            'embed',
            'hr',
            'img',
            'input',
            'link',
            'meta',
            'param',
            'source',
            'track',
            'wbr',
        ].includes(tag))
            return start;
        const parent = tag === 'template' ? element.content : element;
        const content = ['script', 'style'].includes(tag)
            ? (element.textContent ?? '')
            : Array.from(parent.childNodes).map(serialize).join('');
        return `${start}${content}</${tag}>`;
    };
    const richText = (value, format, media) => {
        const approvedImages = new Set();
        const asset = (value) => {
            const url = media(value);
            if (url)
                approvedImages.add(url);
            return url;
        };
        const mediaUrl = (blobId) => asset({ blobId }) ?? fail('document attachment URL resolver required');
        if (format === 'markdown') {
            if (typeof value !== 'string' || !options.markdown)
                fail('Markdown parser required');
            value = options.markdown(value);
        }
        if (typeof value !== 'string') {
            if (!options.docHtml)
                fail('rich-text requires projected HTML or a literal Doc');
            value = options.docHtml(value, options.urlIssue, mediaUrl, id => {
                const path = options.docLinks?.[id];
                if (typeof path !== 'string' ||
                    !path.startsWith('/') ||
                    options.urlIssue(path))
                    return null;
                return routeUrl(path);
            });
        }
        const template = root.ownerDocument.createElement('template');
        template.innerHTML = value;
        const allowed = new Set([
            'FIGURE',
            'FIGCAPTION',
            'ASIDE',
            'NAV',
            'CITE',
            'ARTICLE',
            'SECTION',
            'P',
            'DIV',
            'SPAN',
            'BR',
            'HR',
            'H1',
            'H2',
            'H3',
            'H4',
            'H5',
            'H6',
            'UL',
            'OL',
            'LI',
            'BLOCKQUOTE',
            'PRE',
            'CODE',
            'STRONG',
            'EM',
            'U',
            'S',
            'A',
            'IMG',
            'TABLE',
            'THEAD',
            'TBODY',
            'TR',
            'TH',
            'TD',
        ]);
        const clean = (element, depth) => {
            check(depth);
            if (!allowed.has(element.tagName))
                fail('unsafe rich-text element');
            for (const attr of Array.from(element.attributes)) {
                if (![
                    'href',
                    'src',
                    'alt',
                    'title',
                    'colspan',
                    'id',
                    'aria-label',
                    'rowspan',
                    'width',
                    'height',
                    'data-block-type',
                    'data-list-type',
                    'data-checked',
                    'data-language',
                    'data-caption',
                    'data-database-title',
                    'data-mime-type',
                ].includes(attr.name) &&
                    !(element.tagName === 'IMG' &&
                        attr.name === 'style' &&
                        /^object-fit:(cover|contain);object-position:[0-9.]+% [0-9.]+%$/.test(attr.value)) &&
                    !(element.tagName === 'CODE' &&
                        attr.name === 'class' &&
                        /^language-[a-zA-Z0-9_-]+$/.test(attr.value)) &&
                    !(['TH', 'TD'].includes(element.tagName) &&
                        attr.name === 'align' &&
                        /^(left|right|center)$/.test(attr.value)))
                    fail('unsafe rich-text attribute');
                if ((attr.name === 'href' || attr.name === 'src') &&
                    options.urlIssue(attr.value, element.tagName === 'A' && attr.name === 'href'
                        ? 'link'
                        : 'resource') &&
                    !(element.tagName === 'IMG' &&
                        attr.name === 'src' &&
                        approvedImages.has(attr.value)))
                    fail('unsafe rich-text URL');
            }
            const href = element.tagName === 'A' ? element.getAttribute('href') : null;
            const base = (options.routeBasePath ?? '').replace(/\/$/, '');
            if (href?.startsWith('/'))
                element.setAttribute(`${reserved}path`, base && (href === base || href.startsWith(base + '/'))
                    ? href.slice(base.length) || '/'
                    : href);
            for (const child of Array.from(element.children))
                clean(child, depth + 1);
        };
        for (const child of Array.from(template.content.children))
            clean(child, 0);
        return Array.from(template.content.childNodes).map(serialize).join('');
    };
    const inspect = (authored = true) => {
        navigation.clear();
        visited = 0;
        sources.clear();
        columns.clear();
        sourceUses.clear();
        listFilters.clear();
        renderedLists.clear();
        validate(root, undefined, 0, authored);
        return [...sources];
    };
    /** Literal lists always read their keys, separately from a mixed source's
     * normal window. Row-dependent filters narrow only exclusively used sources. */
    const inspectListFilters = (authored = true) => {
        inspect(authored);
        const result = [];
        for (const source of sources) {
            const filters = [...(sourceUses.get(source) ?? [])].map(use => listFilters.get(use));
            const literal = filters.filter(filter => filter?.key !== undefined);
            if (literal.length) {
                for (const filter of literal)
                    if (filter)
                        result.push({
                            sourceKey: source,
                            ...filter,
                            ...(literal.length === filters.length
                                ? {}
                                : { includeWindow: true }),
                        });
            }
            else if (filters.length === 1 && filters[0])
                result.push({ sourceKey: source, ...filters[0] });
        }
        return result;
    };
    /** Literal column IDs per source that the template reads; `@id` excluded. */
    const inspectColumns = () => {
        inspect();
        return [...columns.values()];
    };
    /** The page's links; `layoutLandmark` is appBaseLayoutLandmark, passed in
     * because this engine is emitted verbatim and reads nothing outside it. */
    const inspectNavigation = (layoutLandmark) => {
        collectingNavigation = true;
        landmarkOf = layoutLandmark;
        try {
            inspect();
            return [...navigation.values()];
        }
        finally {
            collectingNavigation = false;
            landmarkOf = undefined;
        }
    };
    const render = (datasets) => {
        inspect(false);
        visited = 0;
        deepest = 0;
        renderedRows = 0;
        renderedBytes = 0;
        for (const template of Array.from(root.querySelectorAll('template[data-sl-binding-list]'))) {
            const id = Number(template.getAttribute(`${reserved}list`));
            if (!Number.isSafeInteger(id) || id < 1)
                fail('invalid list identity');
            listSequence = Math.max(listSequence, id);
        }
        // Whoever types into a place is ahead of the stored value: that place
        // keeps its text, and nothing around it moves or goes away meanwhile.
        // Only a page in a browser has such a place.
        const focused = root.ownerDocument?.activeElement;
        const typed = focused?.hasAttribute('contenteditable') ? focused : null;
        const holdsTyped = (node) => typed !== null && node.contains(typed);
        let outputWork = 0;
        const charge = (value) => {
            outputWork += new TextEncoder().encode(value).length;
            renderedBytes = outputWork;
            if (outputWork > options.budgets.bytes)
                fail('output byte budget exceeded (limit binding.bytes)');
        };
        let rowCount = 0;
        const singleRows = new Map();
        for (const source of sources) {
            if (!own(datasets, source))
                fail(`missing source ${source}`);
            const dataset = datasets[source];
            rowCount += dataset.rows.length + (dataset.literalRows?.length ?? 0);
            renderedRows = rowCount;
            singleRows.set(source, options.selectedRows && own(options.selectedRows, source)
                ? dataset.rows.find(row => row.id === options.selectedRows[source])
                : dataset.rows.length === 1
                    ? dataset.rows[0]
                    : undefined);
            for (const rows of [
                dataset.rows,
                ...(dataset.literalRows ? [dataset.literalRows] : []),
            ])
                if (new Set(rows.map(row => row.id)).size !== rows.length)
                    fail('duplicate row identity');
        }
        const valueOf = (source, row, name) => {
            if (!source)
                return fail('source required');
            const dataset = datasets[source];
            if (dataset.status === 'unavailable')
                return null;
            row = row ?? singleRows.get(source);
            if (!row)
                return fail('single-row binding requires an exact row');
            if (name === '@id')
                return row.id;
            if (!dataset.columns.some(column => column.id === name))
                return fail(`unknown column ${name}`);
            return own(row.cells, name) ? (row.cells[name] ?? null) : null;
        };
        const text = (value) => {
            if (value === null)
                return '';
            if (!['string', 'number', 'boolean'].includes(typeof value))
                return fail('text binding requires a scalar');
            return String(value);
        };
        // A select cell holds option IDs; the page shows their labels.
        const label = (source, name, value) => {
            const labels = source
                ? datasets[source].columns.find(column => column.id === name)?.options
                : undefined;
            if (!labels || value === null)
                return value;
            const of = (id) => labels.find(option => option.id === id)?.value ?? text(id);
            return Array.isArray(value) ? value.map(of).join(', ') : of(value);
        };
        // Elements that declare a source, and whether the single row of that
        // source is shown below them. A list row is marked by its list instead.
        const singleRowShown = new Map();
        /** The gaps before and after the drawn rows of list `id`, where they stand. */
        const gapsOf = (list, id) => ['before', 'after'].map(side => Array.from(list.parentElement.children).find(child => child.getAttribute(`${reserved}gap`) === `${id}:${side}`));
        /**
         * A gap of `height` px in the place of rows: an element like the row.
         * Where no row is left out there is none, so the rows stand as without
         * a window (a flex or grid gap, :first-child).
         */
        const gap = (list, id, side, existing, height) => {
            if (height <= 0) {
                existing?.remove();
                return undefined;
            }
            const tag = list.content.firstElementChild.tagName.toLowerCase();
            let element = existing;
            if (!element) {
                element = root.ownerDocument.createElement(tag);
                element.setAttribute(`${reserved}gap`, `${id}:${side}`);
                element.setAttribute('aria-hidden', 'true');
                // A table row holds its height in a cell across the table.
                if (tag === 'tr')
                    element.appendChild(root.ownerDocument.createElement('td'));
            }
            const style = `height:${height}px;padding:0;margin:0;border:0;list-style:none;grid-column:1/-1`;
            const holder = tag === 'tr' ? element.firstElementChild : element;
            if (tag === 'tr')
                holder.setAttribute('colspan', '1000');
            if (holder.getAttribute('style') !== style)
                holder.setAttribute('style', style);
            return element;
        };
        /**
         * The rows of list `id` near its viewport, by lines of as many rows as
         * stand side by side, and the gaps of the lines around them. Rows not
         * read yet are asked for.
         */
        const placeRows = (list, id, 
        /** The window the list reads, by its source (and key). */
        windowKey, rows, offset, totalCount) => {
            const port = options.rowWindow;
            const state = rowWindows.get(id) ?? {
                heights: new Map(),
                estimate: 0,
                columns: 1,
            };
            rowWindows.set(id, state);
            const total = Math.max(totalCount, offset + rows.length);
            const columns = state.columns;
            const lines = Math.ceil(total / columns);
            const [beforeGap, afterGap] = gapsOf(list, id);
            // The list begins at its gap before or its first row; before either is
            // drawn, at the element that holds it.
            const start = beforeGap ??
                Array.from(list.parentElement.children).find(child => child.getAttribute(`${reserved}owner`) === id) ??
                list.parentElement;
            const window = port.place(lines, state.estimate || 1, state.heights);
            const viewport = port.viewport(start);
            const preload = port.preload(viewport.bottom - viewport.top);
            const range = window.range(viewport.top - preload, viewport.bottom + preload);
            // Before the first row is measured, one row is drawn to measure.
            const lastLine = state.estimate
                ? range.end
                : Math.min(range.end, range.first + 1);
            const wantedFrom = range.first * columns;
            const wantedTo = Math.min(total, lastLine * columns);
            if (wantedFrom < offset ||
                (wantedTo > offset + rows.length && offset + rows.length < total))
                port.want(windowKey, wantedFrom);
            // Drawn rows begin and end on a whole line.
            const from = Math.max(wantedFrom, Math.ceil(offset / columns) * columns);
            let to = Math.min(wantedTo, offset + rows.length);
            if (to < total)
                to = Math.floor(to / columns) * columns;
            const firstLine = from < to ? from / columns : range.first;
            const endLine = from < to ? Math.ceil(to / columns) : range.first;
            return {
                rows: from < to ? rows.slice(from - offset, to - offset) : [],
                firstLine,
                before: gap(list, id, 'before', beforeGap, window.topAt(firstLine)),
                after: gap(list, id, 'after', afterGap, window.height - window.topAt(endLine)),
            };
        };
        /**
         * Takes the heights of the drawn rows of list `id` (from line
         * `firstLine` on): a line is as high as from its top to the next one.
         * Rows standing side by side make one line.
         */
        const measureRows = (id, firstLine, drawn) => {
            const state = rowWindows.get(id);
            if (!drawn.length)
                return false;
            const boxes = drawn.map(node => options.rowWindow.box(node));
            let columns = 1;
            while (columns < boxes.length && boxes[columns].top === boxes[0].top)
                columns++;
            let changed = false;
            if (columns !== state.columns && drawn.length > 1) {
                state.columns = columns;
                state.heights.clear();
                changed = true;
            }
            for (let at = 0; at < boxes.length; at += state.columns) {
                const next = boxes[at + state.columns];
                const line = boxes.slice(at, at + state.columns);
                const height = next
                    ? next.top - boxes[at].top
                    : Math.max(...line.map(box => box.height));
                const index = firstLine + at / state.columns;
                if (state.heights.get(index) !== height) {
                    state.heights.set(index, height);
                    changed = true;
                }
            }
            // The first measurement gives the estimate the list draws by at once.
            if (!state.estimate) {
                state.estimate = Math.max(1, state.heights.get(firstLine) ?? 1);
                return true;
            }
            if (changed)
                options.rowWindow.changed();
            return false;
        };
        const walk = (element, source, row, depth, holder, 
        /** The row each surrounding list shows here, by its source. */
        listRows = new Map(), 
        /** Inside a row the engine drew. */
        inRow = false) => {
            if (owned(element, inRow))
                check(depth);
            if (element.hasAttribute(`${prefix}source`)) {
                const declaredSource = element.getAttribute(`${prefix}source`);
                if (declaredSource !== source)
                    row = undefined;
                source = declaredSource;
                holder = element.hasAttribute(`${reserved}owner`) ? undefined : element;
                if (holder)
                    singleRowShown.set(holder, false);
                if (datasets[source]?.status === 'unavailable')
                    element.setAttribute(`${reserved}unavailable`, '');
                else
                    element.removeAttribute(`${reserved}unavailable`);
            }
            if (element.hasAttribute(`${prefix}list`)) {
                const list = element;
                const id = list.getAttribute(`${reserved}list`) ?? String(++listSequence);
                list.setAttribute(`${reserved}list`, id);
                const dataset = datasets[source];
                let rows = dataset.status === 'unavailable'
                    ? []
                    : options.selectedRows && own(options.selectedRows, source)
                        ? dataset.rows.filter(item => item.id === options.selectedRows[source])
                        : dataset.rows;
                const filterSource = element.getAttribute('data-sl-filter-source');
                const filterKey = element.getAttribute('data-sl-filter-key');
                if ((filterSource || filterKey !== null) &&
                    dataset.literalRows &&
                    dataset.status !== 'unavailable')
                    rows = dataset.literalRows;
                // Inside a list of the filter source, its row there filters;
                // elsewhere the single row of that source.
                const filterValue = filterKey ??
                    (filterSource
                        ? valueOf(filterSource, listRows.get(filterSource), element.getAttribute('data-sl-filter-value'))
                        : null);
                if (filterSource || filterKey !== null) {
                    const field = element.getAttribute('data-sl-filter-field');
                    rows =
                        filterValue === null
                            ? []
                            : rows.filter(item => {
                                const cell = valueOf(source, item, field);
                                return Array.isArray(cell)
                                    ? cell.includes(filterValue)
                                    : cell === filterValue;
                            });
                }
                // Draws the rows; true when it measured the first row of a window
                // list, which then draws the rows of its viewport in the same pass.
                const draw = () => {
                    const existing = new Map();
                    for (const sibling of Array.from(list.parentElement.children)) {
                        if (sibling.getAttribute(`${reserved}owner`) === id)
                            existing.set(sibling.getAttribute(`${reserved}row`), sibling);
                    }
                    // A list of the window of its source (no row of the route, no
                    // filter) or of a key that reads its own window (a literal key, or
                    // the value of the row it filters by) draws the rows near its
                    // viewport.
                    const keyWindow = filterValue === null || filterValue === undefined
                        ? undefined
                        : JSON.stringify([
                            element.getAttribute('data-sl-filter-field'),
                            String(filterValue),
                        ]);
                    const literalWindow = keyWindow === undefined
                        ? undefined
                        : dataset.literalWindows?.[keyWindow];
                    const rowsById = literalWindow && new Map(rows.map(item => [item.id, item]));
                    const placed = !options.rowWindow ||
                        dataset.status === 'unavailable' ||
                        (options.selectedRows && own(options.selectedRows, source))
                        ? undefined
                        : literalWindow
                            ? placeRows(list, id, `${source}:${keyWindow}`, literalWindow.rowIds.map(rowId => rowsById.get(rowId) ??
                                fail('literal window row is missing')), literalWindow.offset, literalWindow.totalCount)
                            : filterKey === null && !filterSource
                                ? placeRows(list, id, source, dataset.rows, dataset.offset ?? 0, dataset.totalCount ?? 0)
                                : undefined;
                    if (!placed)
                        for (const gap of gapsOf(list, id))
                            if (gap)
                                gap.remove();
                    const drawn = [];
                    const templateBytes = serialize(list.content.firstElementChild);
                    let anchor = placed?.before ?? list;
                    if (placed?.before && list.nextSibling !== placed.before)
                        list.parentNode.insertBefore(placed.before, list.nextSibling);
                    for (const item of placed ? placed.rows : rows) {
                        charge(templateBytes);
                        let node = existing.get(item.id);
                        if (!node) {
                            node = list.content.firstElementChild.cloneNode(true);
                            if (!node.hasAttribute(`${prefix}source`))
                                node.setAttribute(`${prefix}source`, source);
                            node.setAttribute(`${reserved}owner`, id);
                            node.setAttribute(`${reserved}row`, item.id);
                        }
                        existing.delete(item.id);
                        if (anchor.nextSibling !== node && !holdsTyped(node))
                            anchor.parentNode.insertBefore(node, anchor.nextSibling);
                        walk(node, source, item, depth + 1, undefined, new Map(listRows).set(source, item), true);
                        anchor = node;
                        drawn.push(node);
                    }
                    for (const node of existing.values())
                        if (!holdsTyped(node))
                            node.remove();
                    if (placed) {
                        if (placed.after && anchor.nextSibling !== placed.after)
                            anchor.parentNode.insertBefore(placed.after, anchor.nextSibling);
                        return measureRows(id, placed.firstLine, drawn);
                    }
                    return false;
                };
                if (draw())
                    draw();
                return;
            }
            if (element.hasAttribute('data-sl-count')) {
                const dataset = datasets[source];
                let count = '';
                if (dataset.status !== 'unavailable') {
                    const field = element.getAttribute('data-sl-filter-field');
                    const key = element.getAttribute('data-sl-filter-key');
                    const value = dataset.counts?.[JSON.stringify([field, key])];
                    if (typeof value !== 'number' ||
                        !Number.isSafeInteger(value) ||
                        value < 0)
                        fail('count aggregate is missing');
                    count = String(value);
                }
                charge(count);
                if (element.textContent !== count)
                    element.textContent = count;
                return;
            }
            const staticRoute = element.getAttribute('data-sl-route');
            if (staticRoute !== null && !element.hasAttribute('data-sl-attr-href')) {
                if (options.routeBasePath === undefined)
                    fail('route base required');
                element.setAttribute('href', routeUrl(staticRoute));
                element.setAttribute(`${reserved}path`, staticRoute);
            }
            let hasContent = false;
            for (const attr of Array.from(element.attributes)) {
                if (!attr.name.startsWith(prefix))
                    continue;
                const kind = attr.name.slice(prefix.length);
                if (!fields.has(kind) &&
                    !kind.startsWith('attr-') &&
                    !kind.startsWith('asset-'))
                    continue;
                const value = valueOf(source, row, attr.value);
                if (!row && holder && datasets[source].status !== 'unavailable')
                    singleRowShown.set(holder, true);
                if (kind === 'if') {
                    if (value)
                        element.removeAttribute('hidden');
                    else if (!holdsTyped(element))
                        element.setAttribute('hidden', '');
                }
                else if (kind === 'text') {
                    const next = text(label(source, attr.value, value));
                    charge(next);
                    if (element !== typed && element.textContent !== next)
                        element.textContent = next;
                    hasContent = true;
                }
                else if (kind === 'json-ld') {
                    if (value !== null &&
                        (typeof value !== 'object' || Array.isArray(value)))
                        fail('JSON-LD requires an object');
                    const next = value === null
                        ? ''
                        : JSON.stringify(value)
                            .replace(/</g, '\\u003c')
                            .replace(/>/g, '\\u003e')
                            .replace(/&/g, '\\u0026');
                    charge(next);
                    let script = element.nextElementSibling;
                    if (!script || !script.hasAttribute(`${reserved}json-ld`)) {
                        script = root.ownerDocument.createElement('script');
                        script.setAttribute('type', 'application/ld+json');
                        script.setAttribute(`${reserved}json-ld`, '');
                        element.parentNode.insertBefore(script, element.nextSibling);
                    }
                    if (script.textContent !== next)
                        script.textContent = next;
                    hasContent = true;
                }
                else if (kind === 'rich-text') {
                    const next = value === null
                        ? ''
                        : richText(value, element.getAttribute('data-sl-format'), media => {
                            const owner = row ?? singleRows.get(source);
                            return (options.assetUrl?.(media, source, owner.id, attr.value) ?? null);
                        });
                    charge(next);
                    if (element !== typed &&
                        Array.from(element.childNodes).map(serialize).join('') !== next)
                        element.innerHTML = next;
                    hasContent = true;
                }
                else if (kind.startsWith('asset-')) {
                    const name = kind.slice(6);
                    const owner = row ?? singleRows.get(source);
                    const next = value === null
                        ? null
                        : options.assetUrl
                            ? options.assetUrl(value, source, owner.id, attr.value)
                            : fail('attachment URL resolver required');
                    if (next === null)
                        element.removeAttribute(name);
                    else {
                        charge(next);
                        const current = element.getAttribute(name);
                        // An address set again loads its file again: a playing video starts
                        // over. A link loads nothing and takes the newest address.
                        if (name === 'href' ||
                            current === null ||
                            !(options.sameAsset?.(current, next) ?? current === next))
                            // The platform resolver enforces attachment admission paths or signed image origin policy.
                            element.setAttribute(name, next);
                    }
                }
                else {
                    const name = kind.slice(5);
                    let next = text(value);
                    const route = element.getAttribute('data-sl-route');
                    if (name === 'href' && route) {
                        if (value === null)
                            element.removeAttribute(`${reserved}path`);
                        else {
                            if (options.routeBasePath === undefined ||
                                !next ||
                                next.includes('/') ||
                                next === '.' ||
                                next === '..')
                                fail('invalid route slug');
                            const logical = route.replace(':slug', encodeURIComponent(next));
                            element.setAttribute(`${reserved}path`, logical);
                            next = routeUrl(logical);
                        }
                    }
                    charge(next);
                    // A route link is the platform's own mount path plus an encoded
                    // slug, like a static route; the App URL policy would refuse the
                    // author preview mount under /api.
                    if (!(name === 'href' && route) &&
                        (name === 'href' ||
                            name === 'src' ||
                            (name === 'content' &&
                                /(?:image|url)$/.test(element.getAttribute('property') ??
                                    element.getAttribute('name') ??
                                    ''))) &&
                        options.urlIssue(next, element.tagName === 'A' && name === 'href' ? 'link' : 'resource'))
                        fail('unsafe bound URL');
                    if (value === null)
                        element.removeAttribute(name);
                    else if (element.getAttribute(name) !== next)
                        element.setAttribute(name, next);
                }
            }
            if (!hasContent)
                for (const child of Array.from(element.children)) {
                    if (!child.hasAttribute(`${reserved}owner`))
                        walk(child, source, row, depth + 1, holder, listRows, inRow);
                }
        };
        walk(root, undefined, undefined, 0);
        // The same mark a list row carries, so a fixed row or a detail page can
        // be told apart by the row it shows.
        for (const [holder, shown] of singleRowShown) {
            const single = shown
                ? singleRows.get(holder.getAttribute(`${prefix}source`))
                : undefined;
            if (!single)
                holder.removeAttribute(`${reserved}row`);
            else if (holder.getAttribute(`${reserved}row`) !== single.id)
                holder.setAttribute(`${reserved}row`, single.id);
        }
    };
    const inspectCounts = (authored = true) => {
        inspect(authored);
        const counts = new Map();
        for (const element of [
            ...(root.hasAttribute('data-sl-count') ? [root] : []),
            ...Array.from(root.querySelectorAll('[data-sl-count]')),
        ]) {
            const sourceKey = element
                .closest('[data-sl-source]')
                .getAttribute('data-sl-source');
            const count = {
                sourceKey,
                field: element.getAttribute('data-sl-filter-field'),
                key: element.getAttribute('data-sl-filter-key'),
            };
            counts.set(JSON.stringify(count), count);
        }
        return [...counts.values()];
    };
    /** The page as HTML, for a server that sends it; a browser never needs it. */
    const output = () => {
        const html = serialize(root);
        if (new TextEncoder().encode(html).length > options.budgets.bytes)
            fail('output byte budget exceeded (limit binding.bytes)');
        return html;
    };
    /** What the last render took, also one that stopped at a budget. */
    const usage = () => ({
        nodes: visited,
        rows: renderedRows,
        bytes: renderedBytes,
        depth: deepest,
    });
    return {
        inspect,
        inspectColumns,
        inspectCounts,
        inspectListFilters,
        inspectNavigation,
        render,
        serialize: output,
        usage,
    };
}
